import { CONFIG_OPTION_TOAST_ERROR, CONFIG_OPTION_TOAST_NORMAL } from "common/toast";
import DropdownOptionNew from "components/Common/DropdownOptionNew";
import SearchAddress from "components/Common/SearchAddress";
import UploadFile from "components/Common/UploadFile";
import { useRole } from "components/Hooks/UserHooks";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import { useFormik } from "formik";
import { isHavePermissionRole, ROLES_FOR_APP } from "helpers/role";
import React, { useEffect, useState } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import * as Yup from "yup";
import DatePicker, { registerLocale } from "react-datepicker";
import { COLOR_MILITARY_STATUS, LIST_OPTION_GENDER, LIST_OPTION_MILITARY_STATUS, LIST_OPTION_SNS_TYPE, LIST_OPTION_VOICE_RANGE } from "helpers/constans";
import DropdownStatusNew from "components/Common/DropdownStatusNew";
import { formatAddressByNaver, formatDateStringToKorea, formatISOString } from "helpers/format";
import addressApi from "api/addressApi";
import { updateProfileUser } from "store/thunks";

registerLocale("en", en);
registerLocale("ko", ko);

const ProfileInfoTab = ({data, handleUpdateSuccess} : any) => {
  const { t, i18n } = useTranslation();
  const { userPermissions } = useRole();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [isOpenSearchAddress, setIsOpenSearchAddress] =
    useState<boolean>(false);
  const [isLoadingMap, setLoadingMap] = useState<boolean>(false);

  const handleSubmit = async (values: any) => {
    if(!data?.id) return;

    try {
      setIsLoading((_prev) => true);
      const formatValues = {
        image_url: values?.logo?.length > 0 ? values?.logo?.[0]?.fullPath : "",
        nickname: values?.nickname || "",
        fullname: values?.fullname || "",
        gender: values?.gender || "",
        phone_number: values?.phone_number || "",
        email: values?.email || "",
        socials: (values?.sns_type && values?.sns_link) ? [
          {
            "sns": values?.sns_type,
            "url": values?.sns_link
          }
        ] : null,
        height: Number(values?.height) || null,
        weight: Number(values?.weight) || null,
        address: values?.address || "",
        short_address: values?.short_address || "",
        military_status: values?.military_service || "",
        voice_range: values?.voice_range || "",
        bio: values?.introduce || "",
        birthday: values?.birthdate
          ? formatISOString(values?.birthdate)
          : null,
      };
      
      const response: any = await updateProfileUser(data?.id, formatValues);
      
      if (response?.statusCode) {
        setIsLoading((_prev) => false);
        toast(
            `${t("The process has been completed.")}`,
            CONFIG_OPTION_TOAST_NORMAL
        );
        handleUpdateSuccess && handleUpdateSuccess();
      } else {
        setIsLoading((_prev) => false);
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      setIsLoading((_prev) => false);
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };

  const validationSchema = Yup.object({
    logo: Yup.array()
      .of(
        Yup.object({
          filePath: Yup.string().nullable(),
        }).required(`${t("This field is required")}`),
      ).required(`${t("This field is required")}`),
    nickname: Yup.string().required(`${t("This field is required")}`),
    fullname: Yup.string().required(`${t("This field is required")}`),
    gender: Yup.string().required(`${t("This field is required")}`),
    voice_range: Yup.string().required(`${t("This field is required")}`),
    phone_number: Yup.string().required(`${t("This field is required")}`)
      .matches(/^\d{1,11}$/, t("Phone number must be up to 11 digits")),
    sns_type: Yup.string().nullable(),
    sns_link: Yup.string().when("sns_type", {
      is: (value: any) => !!value,
      then: (rule) => {
        return rule.test(
          "sns_type",
          `${t("This field is required")}`,
          (target) => !!target?.trim()
        );
      },
    }),
    address: Yup.string().required(`${t("This field is required")}`),
    short_address: Yup.string().nullable(),
    military_service: Yup.string().required(`${t("This field is required")}`),
    birthdate: Yup.string().required(`${t("This field is required")}`),
  });

  const formik = useFormik({
    initialValues: {
      logo: null,
      nickname: "",
      fullname: "",
      gender: "male",
      birthdate: new Date() || "",
      phone_number: "",
      email: "",
      sns_type: "",
      sns_link: "",
      height: 0,
      weight: 0,
      address: "",
      short_address: "",
      military_service: "",
      voice_range: "PRIVATE",
      introduce: "",
    },
    validationSchema,
    onSubmit: handleSubmit,
  });

  const handleCloseModalAddress = () => {
    setIsOpenSearchAddress(false);
  };

  const handleOpenSearchAddress = () => {
    setIsOpenSearchAddress(true);
  };

  const getLatLngByAddress = async (address: string) => {
    try {
      if (address) {
        const data: any = await addressApi.getGEOByAddress({
          query: encodeURIComponent(address),
        });
        let dataAddress = data?.data?.addresses;
        const formatAddress = formatAddressByNaver(dataAddress);

        if (formatAddress) {
          formik?.setFieldValue("address", formatAddress?.address || '');
          formik?.setFieldValue("short_address", formatAddress?.short_address || '');
        }
      }
    } catch (error) {
      //
      console.log(error)
    }
  };

  const onSetValueAddress = async (data: any) => {
    if (data) {
      setLoadingMap(true);
      const res = await getLatLngByAddress(data);
      setLoadingMap(false);
    }
    setIsOpenSearchAddress(false);
  };

  useEffect(() => {
    if(data) {
      formik.setValues({
        ...data,
        email: data?.profile?.email,
        nickname: data?.profile?.nickname,
        fullname: data?.profile?.fullname,
        gender: data?.profile?.gender,
        phone_number: data?.profile?.phone_number,
        sns_type: data?.profile?.socials?.[0]?.sns,
        sns_link: data?.profile?.socials?.[0]?.url,
        military_service: data?.profile?.military_service,
        voice_range: data?.profile?.vocal_range,
        height: data?.profile?.height,
        weight: data?.profile?.weight,
        address: data?.address?.address,
        short_address: data?.address?.short_address,
        introduce: data?.profile?.bio,
        logo: data?.profile?.image_url ? [{fullPath: data?.profile?.image_url}] : '',
        birthdate: data?.profile?.date_of_birth ? new Date(formatDateStringToKorea(data?.profile?.date_of_birth)) : null,
        last_activity_time: data?.last_activity_time ? new Date(formatDateStringToKorea(data.last_activity_time)) : null,
        signup_date: data?.signup_date ? new Date(formatDateStringToKorea(data.signup_date)) : null,
      })
    }
  }, [data]);
  
  useEffect(() => {
    document.title = `${t("Profile Info")} | ShowStar`;
    document.body.classList.remove("vertical-sidebar-enable");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);
  
  return (
    <React.Fragment>
      <Row>
        <Col lg={12} className="mt-4 px-4">
          <form onSubmit={formik.handleSubmit}>
            <div
              className="d-flex flex-column mb-4 pt-3 pb-1 px-3 position-relative"
              style={{
                border: "1px dashed var(--vz-primary)",
                borderRadius: "4px",
              }}
            >
              <span
                className="text-primary position-absolute fs-13 px-1"
                style={{
                  backgroundColor: "var(--vz-secondary-bg)",
                  top: "0px",
                  transform: "translateY(-50%)",
                }}
              >
                {t("Info")}
              </span>
              <div className="d-flex flex-column flex-lg-row gap-3 gap-lg-4">
                <div>
                  <div className="mb-3" style={{width: '200px'}}>
                    <Label htmlFor="contact_name" className="form-label">
                      {t("avatar")} <span className="text-danger">*</span>
                    </Label>
                    <UploadFile
                      formik={formik}
                      fieldValue="logo"
                      isMultiple={false}
                      isUploadMore={
                        !formik?.values?.logo ||
                        !(formik?.values?.logo as any)?.length
                      }
                    />
                    {formik.touched.logo && formik.errors.logo ? (
                      <div className="text-danger mt-2">
                        {formik.errors.logo}
                      </div>
                    ) : null}
                  </div>
                </div>
                <div className="flex-grow-1">
                  <Row>
                    <Col md={6} lg={6}>
                      <div className="mb-3">
                        <Label htmlFor="nickname" className="form-label">
                          {t("Nickname")} <span className="text-danger">*</span>
                        </Label>
                        <Input
                          name="nickname"
                          type="text"
                          className="form-control"
                          id="nickname"
                          placeholder={t("Nickname")}
                          value={formik?.values?.nickname}
                          onChange={(event: any) =>
                            formik.setFieldValue(
                              "nickname",
                              event?.target?.value
                            )
                          }
                        />
                        {formik.touched.nickname && formik.errors.nickname ? (
                          <div className="text-danger mt-2">
                            {formik.errors.nickname}
                          </div>
                        ) : null}
                      </div>
                    </Col>
                    <Col md={6} lg={6}>
                      <div className="mb-3">
                        <Label htmlFor="fullname" className="form-label">
                          {t("Full Name")}{" "}
                          <span className="text-danger">*</span>
                        </Label>
                        <Input
                          name="fullname"
                          type="text"
                          className="form-control"
                          id="fullname"
                          placeholder={t("Full Name")}
                          value={formik?.values?.fullname}
                          onChange={(event: any) =>
                            formik.setFieldValue(
                              "fullname",
                              event?.target?.value
                            )
                          }
                        />
                        {formik.touched.fullname && formik.errors.fullname ? (
                          <div className="text-danger mt-2">
                            {formik.errors.fullname}
                          </div>
                        ) : null}
                      </div>
                    </Col>
                    <Col md={6} lg={6}>
                      <div className="mb-3">
                        <Label htmlFor="email" className="form-label">
                          {t("Email")}
                        </Label>
                        <Input
                          name="email"
                          type="text"
                          className="form-control"
                          id="email"
                          placeholder={t("Email")}
                          value={formik?.values?.email}
                          onChange={(event: any) =>
                            formik.setFieldValue("email", event?.target?.value)
                          }
                        />
                        {formik.touched.email && formik.errors.email ? (
                          <div className="text-danger mt-2">
                            {formik.errors.email}
                          </div>
                        ) : null}
                      </div>
                    </Col>
                    <Col md={6} lg={6}>
                      <div className="mb-3">
                        <Label htmlFor="phone_number" className="form-label">
                          {t("Phone Number")} {" "}
                          <span className="text-danger">*</span>
                        </Label>
                        <Input
                          name="phone_number"
                          type="text"
                          className="form-control"
                          id="phone_number"
                          placeholder={t("Phone Number")}
                          value={formik?.values?.phone_number}
                          onChange={(event: any) =>
                            formik.setFieldValue(
                              "phone_number",
                              event?.target?.value
                            )
                          }
                        />
                        {formik.touched.phone_number &&
                        formik.errors.phone_number ? (
                          <div className="text-danger mt-2">
                            {formik.errors.phone_number}
                          </div>
                        ) : null}
                      </div>
                    </Col>
                    <Col md={6} lg={6}>
                      <div className="mb-3">
                        <Label htmlFor="gender" className="form-label">
                          {t("gender")} <span className="text-danger">*</span>
                        </Label>
                        <DropdownOptionNew
                          name="gender"
                          dataList={LIST_OPTION_GENDER}
                          placeholder={`${t("gender")}...`}
                          className="dropdown-status-rounded"
                          classNamePrefix="name-prefix"
                          initialValue={formik?.values?.gender}
                          onChangeSelect={(e: any) =>
                            formik.setFieldValue("gender", e?.value)
                          }
                          isHasOptionAll={false}
                        />
                        {formik.touched.gender && formik.errors.gender ? (
                          <div className="text-danger mt-2">
                            {formik.errors.gender}
                          </div>
                        ) : null}
                      </div>
                    </Col>
                    <Col md={6} lg={6} className="datepicker-time">
                      <div className="mb-3">
                        <Label htmlFor="birthdate" className="form-label">
                          {t("Birthdate")}{" "}
                          <span className="text-danger">*</span>
                        </Label>

                        <div className="w-100">
                          <DatePicker
                            name={"birthdate"}
                            className="form-control"
                            selected={formik?.values?.birthdate}
                            placeholderText={t("Birthdate")}
                            onChange={(date: any) => {
                              formik.setFieldValue("birthdate", date);
                            }}
                            dateFormat="yyyy-MM-dd"
                            isClearable={true}
                          />
                        </div>

                        {formik.touched.birthdate && formik.errors.birthdate ? (
                          <div className="text-danger mt-2">
                            {String(formik.errors.birthdate)}
                          </div>
                        ) : null}
                      </div>
                    </Col>
                    <Col md={6} lg={6}>
                      <div className="mb-3">
                        <Label htmlFor="sns_type" className="form-label">
                          {t("SNS Type")}
                        </Label>
                        <DropdownOptionNew
                          name="sns_type"
                          dataList={LIST_OPTION_SNS_TYPE}
                          placeholder={`${t("SNS Type")}...`}
                          className="dropdown-status-rounded"
                          classNamePrefix="name-prefix"
                          initialValue={formik?.values?.sns_type}
                          onChangeSelect={(e: any) => {
                            if(!e?.value) {
                              formik.setFieldValue("sns_link", "")
                            }
                            formik.setFieldValue("sns_type", e?.value)
                          }}
                          isHasOptionAll={false}
                          lstImage={true}
                          isClearable={true}
                        />
                        {formik.touched.sns_type && formik.errors.sns_type ? (
                          <div className="text-danger mt-2">
                            {formik.errors.sns_type}
                          </div>
                        ) : null}
                      </div>
                      {formik.touched.sns_type && formik.errors.sns_type ? (
                        <div className="text-danger mt-2">
                          {formik.errors.sns_type}
                        </div>
                      ) : null}
                    </Col>
                    <Col md={6} lg={6}>
                      <div className="mb-3">
                        <Label htmlFor="sns_link" className="form-label">
                          {t("SNS Link")}
                        </Label>
                        <Input
                          name="sns_link"
                          type="text"
                          className="form-control"
                          id="sns_link"
                          placeholder={t("SNS Link")}
                          value={formik?.values?.sns_link}
                          disabled={!formik?.values?.sns_type}
                          onChange={(event: any) =>
                            formik.setFieldValue(
                              "sns_link",
                              event?.target?.value
                            )
                          }
                        />
                        {formik.touched.sns_link && formik.errors.sns_link ? (
                          <div className="text-danger mt-2">
                            {formik.errors.sns_link}
                          </div>
                        ) : null}
                      </div>
                    </Col>
                    <Col md={6} lg={6}>
                      <div className="mb-3">
                        <Label htmlFor="height" className="form-label">
                          {t("Height")}
                        </Label>
                        <Input
                          name="height"
                          type="text"
                          className="form-control"
                          id="height"
                          placeholder={t("Height")}
                          value={formik?.values?.height}
                          onChange={(event: any) =>
                            formik.setFieldValue("height", event?.target?.value)
                          }
                        />
                        {formik.touched.height && formik.errors.height ? (
                          <div className="text-danger mt-2">
                            {formik.errors.height}
                          </div>
                        ) : null}
                      </div>
                    </Col>

                    <Col md={6} lg={6}>
                      <div className="mb-3">
                        <Label htmlFor="weight" className="form-label">
                          {t("Weight")}
                        </Label>
                        <Input
                          name="weight"
                          type="text"
                          className="form-control"
                          id="weight"
                          placeholder={t("Weight")}
                          value={formik?.values?.weight}
                          onChange={(event: any) =>
                            formik.setFieldValue("weight", event?.target?.value)
                          }
                        />
                        {formik.touched.short_address &&
                        formik.errors.short_address ? (
                          <div className="text-danger mt-2">
                            {formik.errors.short_address}
                          </div>
                        ) : null}
                      </div>
                    </Col>
                    <Col lg={12}>
                      <div className="mb-3">
                        <Label htmlFor="address" className="form-label">
                          {t("Address")}
                          <span className="text-danger"> *</span>
                        </Label>

                        <div className="d-flex mb-2 gap-2">
                          <Input
                            name="address"
                            type="text"
                            className="form-control"
                            id="address"
                            placeholder={t("Address")}
                            value={formik?.values?.address}
                            onChange={(event: any) =>
                              formik.setFieldValue(
                                "address",
                                event?.target?.value
                              )
                            }
                          />
                          <button
                            type="button"
                            className="btn btn-primary whitespace-nowrap"
                            onClick={handleOpenSearchAddress}
                          >
                            {t("Find Address")}
                          </button>
                        </div>

                        {formik.touched.address && formik.errors.address ? (
                          <div className="text-danger mt-2">
                            {formik.errors.address}
                          </div>
                        ) : null}
                      </div>
                    </Col>

                    <Col lg={12}>
                      <div className="mb-3">
                        <Label htmlFor="address" className="form-label">
                          {t("Short Address")}
                        </Label>
                        <Input
                          name="short_address"
                          type="text"
                          className="form-control"
                          id="short_address"
                          placeholder={t("Short Address")}
                          value={formik?.values?.short_address}
                          onChange={(event: any) =>
                            formik.setFieldValue(
                              "short_address",
                              event?.target?.value
                            )
                          }
                        />
                        {formik.touched.short_address &&
                        formik.errors.short_address ? (
                          <div className="text-danger mt-2">
                            {formik.errors.short_address}
                          </div>
                        ) : null}
                      </div>
                    </Col>

                    <Col md={6} lg={6}>
                      <div className="mb-3">
                        <Label htmlFor="military_service" className="form-label">
                          {t("Military Status")}{" "}
                          <span className="text-danger">*</span>
                        </Label>
                        <DropdownStatusNew
                          name="military_service"
                          dataList={LIST_OPTION_MILITARY_STATUS}
                          placeholder={`${t("Military Status")}...`}
                          className="dropdown-status-rounded"
                          classNamePrefix="name-prefix"
                          initialValue={formik?.values?.military_service}
                          onChangeSelect={(e: any) =>
                            formik.setFieldValue("military_service", e?.value)
                          }
                          isHasOptionAll={false}
                          colors={COLOR_MILITARY_STATUS}
                        />
                        {formik.touched.military_service &&
                        formik.errors.military_service ? (
                          <div className="text-danger mt-2">
                            {formik.errors.military_service}
                          </div>
                        ) : null}
                      </div>
                    </Col>
                    <Col md={6} lg={6}>
                      <div className="mb-3">
                        <Label htmlFor="voice_range" className="form-label">
                          {t("Voice Range")}{" "}
                          <span className="text-danger">*</span>
                        </Label>
                        <DropdownOptionNew
                          name="voice_range"
                          dataList={LIST_OPTION_VOICE_RANGE}
                          placeholder={`${t("Voice Range")}...`}
                          className="dropdown-status-rounded"
                          classNamePrefix="name-prefix"
                          initialValue={formik?.values?.voice_range}
                          onChangeSelect={(e: any) =>
                            formik.setFieldValue("voice_range", e?.value)
                          }
                          isHasOptionAll={false}
                        />
                        {formik.touched.voice_range &&
                        formik.errors.voice_range ? (
                          <div className="text-danger mt-2">
                            {formik.errors.voice_range}
                          </div>
                        ) : null}
                      </div>
                    </Col>

                    <Col lg={12}>
                      <div className="mb-3">
                        <Label htmlFor="introduce" className="form-label">
                          {t("Introduce")}
                        </Label>

                        <textarea
                          name="introduce"
                          id="introduce"
                          className="form-control"
                          placeholder="Introduce"
                          value={formik?.values?.introduce}
                          maxLength={300}
                          rows={4}
                          onChange={(event: any) =>
                            formik.setFieldValue(
                              "introduce",
                              event?.target?.value
                            )
                          }
                          onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                            }
                          }}
                        />
                        {formik.touched.introduce && formik.errors.introduce ? (
                          <div className="text-danger mt-2">
                            {formik.errors.introduce}
                          </div>
                        ) : null}
                      </div>
                    </Col>
                  </Row>
                </div>
              </div>

              <Row className="mb-3 mt-2">
                <Col lg={12}>
                  <div className="hstack gap-2 justify-content-end">
                    {isHavePermissionRole(
                      ROLES_FOR_APP.USER_UPDATE,
                      userPermissions
                    ) && (
                      <button type="submit" className="btn btn-primary fs-14">
                        {isLoading ? (
                          <Spinner size="sm me-2"></Spinner>
                        ) : !!data?.id ? (
                          <i className="ri-login-circle-line align-bottom me-2 "></i>
                        ) : (
                          <i className="ri-add-fill align-bottom me-2"></i>
                        )}
                        {data?.id
                          ? t("Button Update")
                          : t("Button Create")}
                      </button>
                    )}
                  </div>
                </Col>
              </Row>
            </div>
          </form>
        </Col>
      </Row>

      {isOpenSearchAddress && (
        <Modal
          isOpen={!!isOpenSearchAddress}
          centered={true}
          size="lg"
          scrollable={false}
        >
          <ModalHeader toggle={handleCloseModalAddress}>
            {"Search Address"}
          </ModalHeader>
          <ModalBody>
            <SearchAddress onSetValueAddress={onSetValueAddress} />
          </ModalBody>
        </Modal>
      )}
    </React.Fragment>
  );
};

export default ProfileInfoTab;
